import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    filterBy,
    FilterDescriptor,
    orderBy,
    SortDescriptor,
    CompositeFilterDescriptor,
} from '@progress/kendo-data-query';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import {
    Grid,
    GridColumn,
    GridColumnMenuFilter,
    GridColumnMenuWrapper,
    GridFilterChangeEvent,
    GridSortChangeEvent,
    GridCellProps,
    GridColumnMenuProps,
    GridCustomRowProps,
    GridCustomHeaderCellProps,
} from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import { filterIcon } from '@progress/kendo-svg-icons';
import cn from 'clsx';
import Button from '@components/core/buttons';
import ExportExcelComponent, { ExportExcelComponentInputColumn } from '@components/core/export-excel';
import Constants from '../../../../utils/constants';
import { LibraryGridProps } from './library-grid.interface';
import { useTranslation } from '@hooks/index';
import { GRID_ROW_TEST_ID_MASTER } from '@utils/automation-test-helpers';
import './styles.scss';
// eslint-disable-next-line max-len
import { getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl } from '@queries/generated/legacy-risk-assessment.generated';

const CustomRow = (props: GridCustomRowProps) => {
    const { trProps, children } = props;

    const extendedProps: Record<string, string | number> = {
        'data-testid': GRID_ROW_TEST_ID_MASTER,
    };

    return (
        <tr {...trProps} {...extendedProps} className={cn(trProps?.className, props?.isAltRow ? 'k-alt' : '')}>
            {children}
        </tr>
    );
};

const LibraryGrid = ({
    items,
    columns,
    itemsType,
    initialSort = [],
    idFieldName = 'id',
    selection,
    exportConfig,
    hasChecked,
}: LibraryGridProps) => {
    const { t } = useTranslation();

    const [sort, setSort] = useState<SortDescriptor[]>(initialSort || []);
    const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>(undefined);
    const [triggerExcelExport, setTriggerExcelExport] = useState(0);

    const { selectedItems, setSelectedItems, showItemsCount } = selection || {};
    const filteredItemsCount = filterBy(items, filter).length;
    const areAllRowsSelected = selectedItems?.length === filteredItemsCount;

    useEffect(() => {
        if (selectedItems?.length && !hasChecked) {
            setSelectedItems?.([]);
        }
    }, [filter]);

    const isFiltered = (field?: string) =>
        filter &&
        (filter.filters || []).find((f1: any) => f1.filters?.find((f2: FilterDescriptor) => f2.field === field));

    const isItemChecked = (item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl) =>
        !!selectedItems?.find((i) => i[idFieldName] === item[idFieldName]);

    const onToggleItem = (
        item: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk_libraryControls_LibraryControl,
    ) => {
        const newItemsArr = [...selectedItems];
        const index: number = selectedItems?.findIndex((i) => i[idFieldName] === item[idFieldName]);

        if (index === -1) {
            newItemsArr.push(item);
        } else {
            newItemsArr.splice(index, 1);
        }

        setSelectedItems?.(newItemsArr);
    };

    const onToggleAll = () => {
        if (areAllRowsSelected) {
            setSelectedItems?.([]);
        } else {
            setSelectedItems?.(filterBy(items, filter));
        }
    };

    return (
        <>
            {!!exportConfig && (
                <div className="hidden-export">
                    <ExportExcelComponent
                        data={filterBy(orderBy(items, sort), filter)}
                        fileName={exportConfig?.fileName ?? 'ExportedData'}
                        columns={columns as ExportExcelComponentInputColumn[]}
                        mapColumns={(exportColumns) => exportColumns}
                        exportTrigger={triggerExcelExport}
                    />
                </div>
            )}
            {!!exportConfig && (
                <div className="export-wrap">
                    <Button
                        id="export-button"
                        className="export-button"
                        disabled={false}
                        onClick={() => setTriggerExcelExport(triggerExcelExport + 1)}
                    >
                        <FontAwesomeIcon icon={['fal', 'file-export']} size="lg" style={{ marginRight: 10 }} />
                        {t('export')}
                    </Button>
                </div>
            )}
            {!!showItemsCount && (
                <div className="list-info">
                    <p className="text-length">
                        {filteredItemsCount} {itemsType || t('ra:recordsDefault')}
                    </p>
                </div>
            )}
            <Grid
                className="library-grid"
                data={filterBy(orderBy(items, sort), filter)}
                sortable
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
                filter={filter}
                filterOperators={Constants.filterOperators}
                onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
                rows={{
                    data: CustomRow,
                }}
                scrollable="scrollable"
            >
                {!!selection && (
                    <GridColumn
                        key="custom"
                        field="custom"
                        orderIndex={0}
                        width={36}
                        cells={{
                            headerCell: ({ thProps }) => (
                                <HeaderThElement
                                    columnId={thProps?.columnId || ''}
                                    {...thProps}
                                    className={thProps?.className}
                                >
                                    <Checkbox
                                        rounded="none"
                                        className="cell-checkbox width-full"
                                        data-testid="check-all-checkbox"
                                        checked={areAllRowsSelected}
                                        onChange={onToggleAll}
                                        size="medium"
                                    />
                                </HeaderThElement>
                            ),
                            data: (props: GridCellProps) => (
                                <td>
                                    <Checkbox
                                        rounded="none"
                                        className="cell-checkbox width-full"
                                        data-testid={`testId-${props.dataItem.id}`}
                                        checked={isItemChecked(props.dataItem)}
                                        onChange={() => onToggleItem(props.dataItem)}
                                        size="medium"
                                    />
                                </td>
                            ),
                        }}
                        sortable={false}
                    />
                )}
                {columns.map((column: any) => (
                    <GridColumn
                        {...column}
                        key={column.id}
                        filterable={false}
                        cells={{
                            ...column.cells,
                            headerCell: (headerCellProps: GridCustomHeaderCellProps) => (
                                <HeaderThElement
                                    columnId={headerCellProps.thProps?.columnId || ''}
                                    {...headerCellProps.thProps}
                                    className={cn(headerCellProps.thProps?.className)}
                                >
                                    {!column.hideFilter && (
                                        <div data-testid={`column-heading-${column.id || column.field}`}>
                                            <GridColumnMenuWrapper
                                                {...headerCellProps.columnMenuWrapperProps}
                                                column={column}
                                                columnMenuIcon={filterIcon}
                                                columnMenu={(columnMenuProps: GridColumnMenuProps) => (
                                                    <div
                                                        className="grid-v2-column-menu-filter"
                                                        data-testid="grid-filter-menu"
                                                    >
                                                        <GridColumnMenuFilter
                                                            {...columnMenuProps}
                                                            expanded
                                                            filterUI={column.filterUi || undefined}
                                                        />
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    )}
                                    <div
                                        id={`title-${column.id}`}
                                        className="header-cell"
                                        onClick={headerCellProps.onClick}
                                    >
                                        <span className="header-cell-title wrap-text">{headerCellProps.children}</span>
                                        {isFiltered(column.field) && (
                                            <div className="k-sort-icon !k-mr-2">
                                                <FontAwesomeIcon icon={['fas', 'filter']} className="icon-smaller" />
                                            </div>
                                        )}
                                    </div>
                                </HeaderThElement>
                            ),
                        }}
                    />
                ))}
            </Grid>
            {!!showItemsCount && (
                <div className="selected-items-container">
                    <p>
                        {selectedItems?.length}/{filteredItemsCount}
                    </p>
                </div>
            )}
        </>
    );
};

export default LibraryGrid;
